import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Spinner, Badge, Nav } from 'react-bootstrap';
import { FaCamera, FaTrash, FaSave, FaPaperPlane, FaFileAlt, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import Webcam from 'react-webcam';
import localforage from 'localforage';
import Select from 'react-select';
import debounce from 'lodash/debounce';
import { incidentReportsAPI, sitesAPI } from '../../utils/api';
import { API_BASE_URL } from '../../config';
import FileUploader from '../common/FileUploader';
import notificationService from '../../services/notificationService';
import './IncidentReport.css';

const IncidentReport = () => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    location: '',
    region: '',
    branch: '',
    siteId: null,
    severity: 'low'
  });
  const [photos, setPhotos] = useState([]);
  const [uploadedPhotos, setUploadedPhotos] = useState([]); // Backend photo paths
  const [uploadedDocuments, setUploadedDocuments] = useState([]); // Backend document paths
  const [draftId, setDraftId] = useState(null);
  const [showCamera, setShowCamera] = useState(false);
  const [facingMode, setFacingMode] = useState('environment'); // 'user' for front, 'environment' for back
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [isDraft, setIsDraft] = useState(false);
  const [draftTimestamp, setDraftTimestamp] = useState(null);
  const [siteOptions, setSiteOptions] = useState([]);
  const [loadingSites, setLoadingSites] = useState(false);
  const [selectedSite, setSelectedSite] = useState(null);
  const webcamRef = useRef(null);
  const uploadedDocumentsRef = useRef([]);

  const DRAFT_KEY = 'incident_report_draft';

  // My Reports tab state
  const [activeTab, setActiveTab] = useState('write');
  const [myReports, setMyReports] = useState([]);
  const [myReportsLoading, setMyReportsLoading] = useState(false);
  const [myReportsError, setMyReportsError] = useState('');
  const [expandedReportId, setExpandedReportId] = useState(null);
  const [reportComments, setReportComments] = useState({});
  const [commentsLoading, setCommentsLoading] = useState({});
  
  // Keep ref in sync with state
  useEffect(() => {
    uploadedDocumentsRef.current = uploadedDocuments;
  }, [uploadedDocuments]);

  // Load draft on component mount
  useEffect(() => {
    loadDraft();
  }, []);

  // Auto-save draft every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      if (formData.title || formData.description || uploadedPhotos.length > 0 || uploadedDocuments.length > 0) {
        saveDraftToBackend();
      }
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, [formData, uploadedPhotos, uploadedDocuments]);

  const loadDraft = async () => {
    try {
      // Try to load from backend first
      const response = await incidentReportsAPI.getDrafts();

      // Handle nested response structure: response.data.data contains the actual drafts array
      const draftsArray = response.data?.data || response.data;

      // Clear local storage since we have backend connectivity
      // This prevents old local drafts from interfering
      await localforage.removeItem(DRAFT_KEY);

      if (draftsArray && Array.isArray(draftsArray) && draftsArray.length > 0) {

        // Sort drafts by updatedAt to get the most recent one
        const sortedDrafts = [...draftsArray].sort((a, b) => {
          const dateA = new Date(a.updatedAt || a.createdAt);
          const dateB = new Date(b.updatedAt || b.createdAt);
          return dateB - dateA; // Descending order (newest first)
        });

        const latestDraft = sortedDrafts[0]; // Get the most recent draft

        setDraftId(latestDraft.id);
        setDraftTimestamp(latestDraft.updatedAt || latestDraft.createdAt);
        setFormData({
          title: latestDraft.title || '',
          description: latestDraft.description || '',
          location: latestDraft.location || '',
          region: latestDraft.region || '',
          branch: latestDraft.branch || '',
          siteId: latestDraft.siteId || null,
          severity: latestDraft.severity || 'low'
        });

        // Set selected site if location exists in draft
        if (latestDraft.location) {
          setSelectedSite({
            value: latestDraft.location,
            label: latestDraft.location,
            region: latestDraft.region || '',
            branch: latestDraft.branch || '',
            id: latestDraft.siteId
          });
        }

        // Load photos from backend
        const backendPhotos = latestDraft.photos || [];
        setUploadedPhotos(backendPhotos);

        // Load documents from backend
        const backendDocuments = latestDraft.documents || [];
        setUploadedDocuments(backendDocuments);

        // Create preview objects for display
        const photoObjects = backendPhotos.map((photoPath, index) => {
          let previewUrl;
          
          if (photoPath.startsWith('http')) {
            // Backend returned full URL - use it but fix duplicate /uploads/uploads/
            previewUrl = photoPath.replace('/uploads/uploads/', '/uploads/');
          } else {
            // Relative path - construct full URL
            previewUrl = `${API_BASE_URL}/${photoPath}`;
          }
          
          return {
            id: Date.now() + index,
            path: photoPath,
            preview: previewUrl,
            timestamp: latestDraft.updatedAt || new Date().toISOString(),
            uploaded: true
          };
        });
        
        setPhotos(photoObjects);
        setIsDraft(true);
      }
    } catch (error) {
      // Only use local storage if backend is completely unreachable
      try {
        const localDraft = await localforage.getItem(DRAFT_KEY);
        if (localDraft) {
          setFormData(localDraft.formData);
          setPhotos(localDraft.photos || []);
          setUploadedPhotos(localDraft.uploadedPhotos || []);
          setUploadedDocuments(localDraft.uploadedDocuments || []);
          
          // Set selected site if location exists in draft
          if (localDraft.formData?.location) {
            setSelectedSite({
              value: localDraft.formData.location,
              label: localDraft.formData.location,
              region: localDraft.formData.region || '',
              branch: localDraft.formData.branch || '',
              id: localDraft.formData.siteId
            });
          }
          
          setIsDraft(true);
        }
      } catch (e) {
        console.error('Error loading local draft:', e);
      }
    }
  };

  const saveDraftToBackend = async () => {
    try {
      // Use ref to get the absolute latest documents state
      const currentDocuments = uploadedDocumentsRef.current;
      
      const draftData = {
        id: draftId, // Include draft ID for updates (null for new drafts)
        title: formData.title,
        description: formData.description,
        location: formData.location,
        region: formData.region,
        branch: formData.branch,
        siteId: formData.siteId,
        severity: formData.severity,
        documents: currentDocuments,
        photos: uploadedPhotos,
        lastModifiedAt: new Date().toISOString()
      };

      const response = await incidentReportsAPI.saveDraft(draftData);

      if (response.data && response.data.id) {
        const newDraftId = response.data.id;
        setDraftId(newDraftId);
        setIsDraft(true);
        return newDraftId; // Return the draft ID
      } else if (response.data && response.data.data && response.data.data.id) {
        // Handle nested response structure
        const newDraftId = response.data.data.id;
        setDraftId(newDraftId);
        setIsDraft(true);
        return newDraftId;
      }
      return null;
    } catch (error) {
      // Fallback to local storage
      await saveDraftLocally();
      return null;
    }
  };

  const saveDraftLocally = async () => {
    try {
      await localforage.setItem(DRAFT_KEY, {
        formData,
        photos,
        uploadedDocuments,
        uploadedPhotos,
        timestamp: new Date().toISOString()
      });
      setIsDraft(true);
    } catch (error) {
      console.error('Error saving draft locally:', error);
    }
  };

  const clearDraft = async () => {
    try {
      // Clear from backend if we have a draft ID
      if (draftId) {
        await incidentReportsAPI.deleteDraft(draftId);
      }
      // Clear local storage
      await localforage.removeItem(DRAFT_KEY);
      setDraftId(null);
      setIsDraft(false);
    } catch (error) {
      // If draft is already deleted (404), treat it as success
      if (error.response && error.response.status === 404) {
        await localforage.removeItem(DRAFT_KEY);
        setDraftId(null);
        setIsDraft(false);
        return; // Don't throw error, this is expected
      }

      // Still clear local state even if backend deletion fails
      await localforage.removeItem(DRAFT_KEY);
      setDraftId(null);
      setIsDraft(false);
      throw error; // Re-throw only for non-404 errors
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Fetch sites from backend based on search term
  const fetchSites = async (searchTerm) => {
    if (!searchTerm || searchTerm.length < 2) {
      setSiteOptions([]);
      return;
    }

    setLoadingSites(true);
    try {
      const response = await sitesAPI.getAll({ search: searchTerm });
      const sites = response.data.data || response.data || [];
      
      const options = sites.map(site => {
        // Extract region name - backend uses regionDetails object
        const regionName = site.regionDetails?.name || 
                          site.Region?.region_name || 
                          site.Region?.name || 
                          site.region_name ||
                          site.region || '';
        
        // Extract branch name - backend uses branchDetails object
        const branchName = site.branchDetails?.name || 
                          site.Branch?.branch_name || 
                          site.Branch?.name || 
                          site.branch_name ||
                          site.branch || '';
        
        return {
          value: site.site_name || site.name,
          label: `${site.site_name || site.name}${regionName ? ` (${regionName})` : ''}`,
          id: site.id,
          region: regionName,
          branch: branchName,
          siteData: site
        };
      });
      
      setSiteOptions(options);
    } catch (error) {
      setSiteOptions([]);
    } finally {
      setLoadingSites(false);
    }
  };

  // Debounced version of fetchSites
  const debouncedFetchSites = useCallback(
    debounce((searchTerm) => {
      fetchSites(searchTerm);
    }, 500),
    []
  );

  // Handle site selection from dropdown
  const handleSiteSelect = (selectedOption) => {
    setSelectedSite(selectedOption);
    setFormData(prev => ({
      ...prev,
      location: selectedOption ? selectedOption.value : '',
      region: selectedOption ? selectedOption.region : '',
      branch: selectedOption ? selectedOption.branch : '',
      siteId: selectedOption ? selectedOption.id : null
    }));
  };

  // Handle manual input in Select
  const handleSiteInputChange = (inputValue) => {
    debouncedFetchSites(inputValue);
  };

  const toggleCamera = () => {
    setShowCamera(!showCamera);
  };

  const switchCamera = async () => {
    // Stop current stream first
    if (webcamRef.current && webcamRef.current.stream) {
      webcamRef.current.stream.getTracks().forEach(track => track.stop());
    }

    // Switch facing mode
    setFacingMode(prev => prev === 'user' ? 'environment' : 'user');
  };

  const capturePhoto = async () => {
    if (webcamRef.current) {
      try {
        setUploadingPhoto(true);

        // Capture screenshot as base64
        const imageSrc = webcamRef.current.getScreenshot({
          width: 1920,
          height: 1080,
          screenshotFormat: 'image/jpeg',
          screenshotQuality: 0.92
        });

        if (imageSrc) {
          // Convert base64 to Blob
          const response = await fetch(imageSrc);
          const blob = await response.blob();

          // Ensure we have a draft first
          let currentDraftId = draftId;
          if (!currentDraftId) {
            currentDraftId = await saveDraftToBackend();

            // If still no draftId, we can't upload photos to backend
            if (!currentDraftId) {
              const photoData = {
                id: Date.now(),
                blob: blob,
                preview: imageSrc,
                timestamp: new Date().toISOString(),
                facingMode: facingMode,
                uploaded: false
              };
              setPhotos(prev => [...prev, photoData]);
              await saveDraftLocally();
              setShowCamera(false);
              setError('Could not save to server. Photo saved locally.');
              setTimeout(() => setError(''), 3000);
              return;
            }
          }

          // Create FormData and upload to backend
          const formData = new FormData();
          formData.append('draftId', currentDraftId); // Required by backend
          formData.append('photos', blob, `incident_${Date.now()}.jpg`);

          try {
            const uploadResponse = await incidentReportsAPI.uploadPhoto(formData);

            // Backend returns { message, photos: [...], totalPhotos }
            if (uploadResponse.data && uploadResponse.data.photos) {
              const uploadedPhotosPaths = uploadResponse.data.photos; // Keep original paths from backend

              // Update uploaded photos list with all photos from backend
              setUploadedPhotos(uploadedPhotosPaths);

              // Add current photo to local preview with backend path
              const newPhotoPath = uploadedPhotosPaths[uploadedPhotosPaths.length - 1]; // Last uploaded photo
              const photoData = {
                id: Date.now(),
                path: newPhotoPath, // Use original path from backend
                preview: imageSrc, // Use the captured image as preview
                timestamp: new Date().toISOString(),
                facingMode: facingMode,
                uploaded: true
              };

              // Add to photos array for display
              setPhotos(prev => [...prev, photoData]);

              setShowCamera(false);
              setSuccess('Photo uploaded successfully!');
              setTimeout(() => setSuccess(''), 3000);
            } else {
              setError('Photo uploaded but path not returned');
            }
          } catch (uploadError) {
            setError(`Failed to upload photo: ${uploadError.response?.data?.message || uploadError.message}. Saving locally...`);

            // Fallback: save locally only
            const photoData = {
              id: Date.now(),
              blob: blob,
              preview: imageSrc,
              timestamp: new Date().toISOString(),
              facingMode: facingMode,
              uploaded: false
            };
            setPhotos(prev => [...prev, photoData]);
            await saveDraftLocally();
          }
        }
      } catch (error) {
        console.error('Error capturing photo:', error);
        setError('Failed to capture photo. Please try again.');
      } finally {
        setUploadingPhoto(false);
      }
    }
  };

  const deletePhoto = async (photoId, photoPath) => {
    try {
      // If photo was uploaded to backend, delete it
      if (photoPath && draftId) {
        await incidentReportsAPI.deletePhoto(photoPath, draftId);

        // Remove from uploadedPhotos array
        setUploadedPhotos(prev => prev.filter(p => p !== photoPath));
      }

      // Remove from local state
      setPhotos(prev => prev.filter(photo => photo.id !== photoId));

      setSuccess('Photo deleted successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(`Failed to delete photo from server: ${error.response?.data?.message || error.message}. Removed locally.`);
      setTimeout(() => setError(''), 3000);

      // Still remove from local state even if backend delete fails
      setPhotos(prev => prev.filter(photo => photo.id !== photoId));
      setUploadedPhotos(prev => prev.filter(p => p !== photoPath));
    }
  };

  // Document upload handlers
  const handleDocumentUploadSuccess = (response) => {
    // Handle different response formats from backend
    const documentData = response.document || response;
    const documentPath = documentData.path || response.path || response.filePath;
    
    if (documentPath) {
      // Store full document metadata object
      const documentMetadata = {
        path: documentPath,
        name: documentData.name || response.originalName || documentPath.split('/').pop(),
        size: documentData.size || response.size || 0,
        type: documentData.type || response.mimeType || 'application/octet-stream',
        uploadedAt: documentData.uploadedAt || new Date().toISOString()
      };
      
      // Update state with new document and trigger draft save after state updates
      setUploadedDocuments(prev => {
        const newDocs = [...prev, documentMetadata];
        
        // Schedule draft save after this state update completes
        setTimeout(() => {
          saveDraftToBackend();
        }, 200);
        
        return newDocs;
      });
      
      setSuccess('Document uploaded successfully!');
      setTimeout(() => setSuccess(''), 3000);
    }
  };

  // Ensure draft exists before document upload
  const handleBeforeDocumentUpload = async () => {
    if (!draftId) {
      await saveDraftToBackend();
    }
    return true;
  };

  const handleDocumentUploadError = (error) => {
    const errorMessage = error.response?.data?.message || error.message || 'Failed to upload document';
    setError(errorMessage);
    setTimeout(() => setError(''), 5000);
  };

  const handleDocumentDeleteSuccess = (documentPath) => {
    setUploadedDocuments(prev => {
      const updatedDocs = prev.filter(d => d.path !== documentPath);
      
      // Schedule draft save after state update completes
      setTimeout(() => {
        saveDraftToBackend();
      }, 200);
      
      return updatedDocs;
    });
    setSuccess('Document deleted successfully!');
    setTimeout(() => setSuccess(''), 3000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!formData.title.trim()) {
      setError('Please provide a title for the incident');
      return;
    }

    if (!formData.description.trim()) {
      setError('Please provide a description of the incident');
      return;
    }

    setLoading(true);

    try {
      // Submit report with backend photo paths
      const submitData = {
        title: formData.title,
        description: formData.description,
        location: formData.location,
        region: formData.region,
        branch: formData.branch,
        siteId: formData.siteId,
        severity: formData.severity,
        status: 'submitted', // Default status when submitting
        photos: uploadedPhotos,
        documents: uploadedDocuments,
        draftId: draftId // Include draft ID if converting from draft
      };

      await incidentReportsAPI.submit(submitData);

      // Trigger notification to admin and sub-admins
      const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
      notificationService.triggerIncidentAction('incident-submitted', {
        personnelName: currentUser?.name || currentUser?.username || 'Personnel',
        personnelId: currentUser?.id || currentUser?.userId,
        incidentTitle: formData.title,
        siteName: selectedSite?.name || 'Unknown Site',
        severity: formData.severity,
        branch: formData.branch,
        region: formData.region
      });

      setSuccess('Incident report submitted successfully!');

      // Clear draft BEFORE clearing state (needs draftId to delete from backend)
      try {
        await clearDraft();
      } catch (clearError) {
        console.error('Failed to clear draft after submission:', clearError);
        // Log error but don't prevent form reset - better to reset form than to block user
        setError('Report submitted, but failed to clear draft. Please refresh the page.');
      }

      // Clear form and local state
      setFormData({
        title: '',
        description: '',
        location: '',
        region: '',
        branch: '',
        siteId: null,
        severity: 'low'
      });
      setPhotos([]);
      setUploadedPhotos([]);
      setUploadedDocuments([]);
      setSelectedSite(null);

      // Scroll to top to show success message
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error('Error submitting incident report:', error);
      setError(error.response?.data?.message || 'Failed to submit incident report. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveDraft = async () => {
    await saveDraftToBackend();
    setSuccess('Draft saved successfully!');
    setTimeout(() => setSuccess(''), 3000);
  };

  const handleDiscardDraft = async () => {
    if (window.confirm('Are you sure you want to discard this draft? This action cannot be undone.')) {
      try {
        await clearDraft();

        // Clear form
        setFormData({
          title: '',
          description: '',
          location: '',
          region: '',
          branch: '',
          siteId: null,
          severity: 'low'
        });
        setPhotos([]);
        setUploadedDocuments([]);
        setUploadedPhotos([]);
        setSelectedSite(null);
        setDraftTimestamp(null);

        setSuccess('Draft discarded successfully!');
        setTimeout(() => setSuccess(''), 3000);
      } catch (error) {
        console.error('Error discarding draft:', error);
        setError('Failed to discard draft. Please try again.');
      }
    }
  };

  const videoConstraints = {
    width: { ideal: 1920 },
    height: { ideal: 1080 },
    facingMode: facingMode,
    aspectRatio: 16 / 9
  };

  // ── My Reports helpers ──────────────────────────────────────────────────────

  const fetchMyReports = async () => {
    setMyReportsLoading(true);
    setMyReportsError('');
    try {
      const response = await incidentReportsAPI.getMyReports();
      const data = response.data?.data || response.data || [];
      setMyReports(Array.isArray(data) ? data : []);
    } catch (err) {
      setMyReportsError('Failed to load your reports. Please try again.');
    } finally {
      setMyReportsLoading(false);
    }
  };

  const fetchCommentsForReport = async (reportId) => {
    setCommentsLoading(prev => ({ ...prev, [reportId]: true }));
    try {
      const response = await incidentReportsAPI.getComments(reportId);
      const data = response.data?.data || response.data || [];
      setReportComments(prev => ({ ...prev, [reportId]: Array.isArray(data) ? data : [] }));
    } catch (err) {
      setReportComments(prev => ({ ...prev, [reportId]: [] }));
    } finally {
      setCommentsLoading(prev => ({ ...prev, [reportId]: false }));
    }
  };

  const handleTabSelect = (tab) => {
    setActiveTab(tab);
    if (tab === 'my-reports') {
      fetchMyReports();
    }
  };

  const toggleExpandReport = (reportId) => {
    if (expandedReportId === reportId) {
      setExpandedReportId(null);
    } else {
      setExpandedReportId(reportId);
      if (!reportComments[reportId]) {
        fetchCommentsForReport(reportId);
      }
    }
  };

  const getSeverityBadgeVariant = (severity) => {
    const map = { low: 'success', medium: 'warning', high: 'danger', critical: 'danger' };
    return map[severity] || 'secondary';
  };

  const getStatusBadgeVariant = (status) => {
    const map = {
      submitted: 'primary',
      pending: 'primary',
      under_investigation: 'danger',
      reviewed: 'danger',
      resolved: 'success',
      dismissed: 'secondary',
      draft: 'secondary'
    };
    return map[status] || 'secondary';
  };

  const getStatusLabel = (status) => {
    const map = {
      submitted: 'Submitted',
      pending: 'Submitted',
      under_investigation: 'Under Investigation',
      reviewed: 'Under Investigation',
      resolved: 'Resolved',
      dismissed: 'Dismissed',
      draft: 'Draft'
    };
    return map[status] || status;
  };

  return (
    <Container className="incident-report-container py-4">
      <Row className="mb-3">
        <Col>
          <h2>Incident Report</h2>
          <p className="text-muted">Document and report incidents with photos and detailed information</p>
        </Col>
      </Row>

      {/* Tab Navigation */}
      <Nav variant="tabs" className="mb-4" activeKey={activeTab} onSelect={handleTabSelect}>
        <Nav.Item>
          <Nav.Link eventKey="write">Write Report</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="my-reports">My Reports</Nav.Link>
        </Nav.Item>
      </Nav>

      {/* ── Write Report Tab ──────────────────────────────────────────────── */}
      {activeTab === 'write' && (
        <div>
      {error && (
        <Alert variant="danger" onClose={() => setError('')} dismissible>
          {error}
        </Alert>
      )}

      {success && (
        <Alert variant="success" onClose={() => setSuccess('')} dismissible>
          {success}
        </Alert>
      )}

      {isDraft && (
        <Alert variant="info" className="d-flex justify-content-between align-items-center">
          <div>
            <strong>Draft Found</strong>
            {draftTimestamp && (
              <span className="ms-2 text-muted">
                Last updated: {new Date(draftTimestamp).toLocaleString()}
              </span>
            )}
            <div className="small mt-1">Continue editing or submit your report.</div>
          </div>
          <Button
            variant="outline-danger"
            size="sm"
            onClick={handleDiscardDraft}
          >
            Discard Draft
          </Button>
        </Alert>
      )}

      <Form onSubmit={handleSubmit}>
        <Card className="mb-4">
          <Card.Body>
            <h5 className="mb-3">Incident Details</h5>

            <Form.Group className="mb-3">
              <Form.Label>Title *</Form.Label>
              <Form.Control
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="Brief title of the incident"
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Description *</Form.Label>
              <Form.Control
                as="textarea"
                rows={5}
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Provide detailed description of what happened..."
                required
              />
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Location</Form.Label>
                  <Select
                    value={selectedSite}
                    onChange={handleSiteSelect}
                    onInputChange={handleSiteInputChange}
                    options={siteOptions}
                    isLoading={loadingSites}
                    isClearable
                    placeholder="Type to search for a site..."
                    noOptionsMessage={({ inputValue }) => 
                      !inputValue || inputValue.length < 2 
                        ? "Type at least 2 characters to search" 
                        : loadingSites 
                          ? "Searching..." 
                          : "No sites found"
                    }
                    loadingMessage={() => "Searching sites..."}
                    styles={{
                      control: (base) => ({
                        ...base,
                        minHeight: '38px',
                        borderColor: '#ced4da',
                        '&:hover': {
                          borderColor: '#86b7fe'
                        }
                      }),
                      menu: (base) => ({
                        ...base,
                        zIndex: 9999
                      })
                    }}
                    className="react-select-container"
                    classNamePrefix="react-select"
                  />
                  {formData.location && !selectedSite && (
                    <Form.Text className="text-muted">
                      Current location: {formData.location}
                    </Form.Text>
                  )}
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Severity Level *</Form.Label>
                  <Form.Select
                    name="severity"
                    value={formData.severity}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="low">Low - Minor incident</option>
                    <option value="medium">Medium - Requires attention</option>
                    <option value="high">High - Urgent</option>
                    <option value="critical">Critical - Emergency</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <Card className="mb-4">
          <Card.Body>
            <h5 className="mb-3">Photo Evidence</h5>

            {!showCamera && (
              <Button
                variant="primary"
                onClick={toggleCamera}
                className="mb-3"
              >
                <FaCamera className="me-2" />
                Open Camera
              </Button>
            )}

            {showCamera && (
              <div className="camera-container mb-3">
                <div className="camera-wrapper">
                  <Webcam
                    ref={webcamRef}
                    audio={false}
                    screenshotFormat="image/jpeg"
                    videoConstraints={videoConstraints}
                    className="webcam-preview"
                    onUserMediaError={(error) => {
                      console.error('Camera error:', error);
                      setError('Unable to access camera. Please check permissions.');
                      setShowCamera(false);
                    }}
                  />
                  <div className="camera-info">
                    <span className="badge bg-dark">
                      {facingMode === 'user' ? 'Front Camera' : 'Back Camera'}
                    </span>
                  </div>
                </div>

                <div className="camera-controls mt-3">
                  <Button
                    variant="secondary"
                    onClick={switchCamera}
                    className="me-2"
                  >
                    Switch Camera
                  </Button>
                  <Button
                    variant="success"
                    onClick={capturePhoto}
                    className="me-2"
                    disabled={uploadingPhoto}
                  >
                    {uploadingPhoto ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <FaCamera className="me-2" />
                        Capture Photo
                      </>
                    )}
                  </Button>
                  <Button
                    variant="danger"
                    onClick={toggleCamera}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}

            {photos.length > 0 && (
              <div className="photos-grid">
                <h6 className="mb-3">Captured Photos ({photos.length})</h6>
                <Row>
                  {photos.map((photo) => {
                    return (
                      <Col md={4} sm={6} key={photo.id} className="mb-3">
                        <Card className="photo-card">
                          <Card.Img
                            variant="top"
                            src={photo.preview}
                            alt="Incident photo"
                            onError={(e) => {
                              e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%23ddd" width="100" height="100"/%3E%3Ctext fill="%23999" x="50%25" y="50%25" text-anchor="middle" dominant-baseline="middle"%3EImage not found%3C/text%3E%3C/svg%3E';
                            }}
                          />
                          <Card.Body className="p-2">
                            <div className="d-flex justify-content-between align-items-center">
                              <small className="text-muted">
                                {new Date(photo.timestamp).toLocaleTimeString()}
                              </small>
                              <Button
                                variant="danger"
                                size="sm"
                                onClick={() => deletePhoto(photo.id, photo.path)}
                              >
                                <FaTrash />
                              </Button>
                            </div>
                            {photo.uploaded === false && (
                              <small className="text-warning d-block mt-1">Local only</small>
                            )}
                          </Card.Body>
                        </Card>
                      </Col>
                    );
                  })}
                </Row>
              </div>
            )}
          </Card.Body>
        </Card>

        <Card className="mb-4">
          <Card.Body>
            <h5 className="mb-3">Document Attachments (Optional)</h5>
            <p className="text-muted small">
              Upload supporting documents such as incident reports, witness statements, or other relevant files.
            </p>
            
            <FileUploader
              reportId={draftId}
              maxFiles={5}
              maxSize={10 * 1024 * 1024} // 10MB
              onUploadSuccess={handleDocumentUploadSuccess}
              onUploadError={handleDocumentUploadError}
              onDeleteSuccess={handleDocumentDeleteSuccess}
              disabled={loading}
              autoUpload={true}
              showUploadButton={false}
              initialFiles={uploadedDocuments.map((doc) => ({
                name: doc.name || doc.path?.split('/').pop(),
                path: doc.path,
                size: doc.size,
                type: doc.type || 'document'
              }))}
            />
          </Card.Body>
        </Card>

        <div className="d-flex gap-2">
          <Button
            type="submit"
            variant="primary"
            disabled={loading}
            className="px-4"
          >
            {loading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Submitting...
              </>
            ) : (
              <>
                <FaPaperPlane className="me-2" />
                Submit Report
              </>
            )}
          </Button>

          <Button
            type="button"
            variant="outline-secondary"
            onClick={handleSaveDraft}
            disabled={loading}
          >
            <FaSave className="me-2" />
            Save Draft
          </Button>
        </div>
      </Form>
        </div>
      )}

      {/* ── My Reports Tab ────────────────────────────────────────────────── */}
      {activeTab === 'my-reports' && (
        <div>
          {myReportsLoading && (
            <div className="text-center py-5">
              <Spinner animation="border" />
              <p className="mt-2 text-muted">Loading your reports...</p>
            </div>
          )}

          {myReportsError && (
            <Alert variant="danger" onClose={() => setMyReportsError('')} dismissible>
              {myReportsError}
            </Alert>
          )}

          {!myReportsLoading && !myReportsError && myReports.length === 0 && (
            <Card>
              <Card.Body className="text-center py-5 text-muted">
                <FaFileAlt size={32} className="mb-3 opacity-50" />
                <p>You have not submitted any incident reports yet.</p>
              </Card.Body>
            </Card>
          )}

          {!myReportsLoading && myReports.map((report) => {
            const isExpanded = expandedReportId === report.id;
            const comments = reportComments[report.id] || [];
            const isCommentsLoading = commentsLoading[report.id] || false;

            return (
              <Card key={report.id} className="mb-3">
                <Card.Header
                  className="d-flex justify-content-between align-items-center"
                  style={{ cursor: 'pointer' }}
                  onClick={() => toggleExpandReport(report.id)}
                >
                  <div>
                    <strong>{report.title}</strong>
                    <span className="ms-3">
                      <Badge bg={getSeverityBadgeVariant(report.severity)} className="me-2">
                        {report.severity?.toUpperCase()}
                      </Badge>
                      <Badge bg={getStatusBadgeVariant(report.status)}>
                        {getStatusLabel(report.status)}
                      </Badge>
                    </span>
                  </div>
                  <div className="d-flex align-items-center gap-3">
                    <small className="text-muted">{new Date(report.createdAt).toLocaleDateString()}</small>
                    {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
                  </div>
                </Card.Header>

                {isExpanded && (
                  <Card.Body>
                    <Row className="mb-2">
                      <Col md={6}>
                        <p className="mb-1"><strong>Location:</strong> {report.location || 'N/A'}</p>
                        <p className="mb-1"><strong>Submitted:</strong> {new Date(report.createdAt).toLocaleString()}</p>
                      </Col>
                      <Col md={6}>
                        <p className="mb-1"><strong>Severity:</strong>{' '}
                          <Badge bg={getSeverityBadgeVariant(report.severity)}>{report.severity}</Badge>
                        </p>
                        <p className="mb-1"><strong>Status:</strong>{' '}
                          <Badge bg={getStatusBadgeVariant(report.status)}>{getStatusLabel(report.status)}</Badge>
                        </p>
                      </Col>
                    </Row>

                    <h6 className="mt-3">Description</h6>
                    <p style={{ whiteSpace: 'pre-wrap' }}>{report.description || 'No description provided.'}</p>

                    {/* Admin Comments */}
                    <hr />
                    <h6>Admin Comments</h6>
                    {isCommentsLoading ? (
                      <div className="text-center py-2">
                        <Spinner animation="border" size="sm" />
                      </div>
                    ) : comments.length === 0 ? (
                      <p className="text-muted small">No admin comments yet.</p>
                    ) : (
                      <div>
                        {comments.map((c, idx) => (
                          <div key={c.id || idx} className="border rounded p-2 mb-2 bg-light">
                            <div className="d-flex justify-content-between align-items-center mb-1">
                              <strong className="small">{c.author?.name || c.adminName || 'Admin'}</strong>
                              <span className="text-muted" style={{ fontSize: '0.75rem' }}>
                                {c.createdAt ? new Date(c.createdAt).toLocaleString() : ''}
                              </span>
                            </div>
                            <p className="mb-0 small" style={{ whiteSpace: 'pre-wrap' }}>{c.body || c.text || c.comment}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </Card.Body>
                )}
              </Card>
            );
          })}
        </div>
      )}

    </Container>
  );
};

export default IncidentReport;
